'use client';

import { useProviderSection } from '@/lib/hooks/useSiteData';
import Link from 'next/link';
import { useParams } from 'next/navigation';

const ProviderSection = () => {
  const params = useParams();
  const currentLang = (params?.lang as string) || 'en';
  const { data: providerData, loading, error } = useProviderSection();

  // Helper function to decode base64 content
  const decodeContent = (base64Content: string) => {
    try {
      return atob(base64Content);
    } catch (error) {
      console.error('Error decoding base64 content:', error);
      return '';
    }
  };

  // Helper function to extract text from HTML content
  const extractTextFromHtml = (htmlContent: string) => {
    // Remove HTML tags and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Show loading state
  if (loading) {
    return (
      <section className="provider-sec">
        <div className="container">
          <div 
            className="provider-box"
            style={{
              backgroundImage: 'url(/images/boxes/provider_bg.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'cover'
            }}
          >
            <div className="row">
              <div className="col-lg-12">
                <div className="content-box">
                  <div className="placeholder-glow">
                    <h2 className="placeholder col-6 mb-3"></h2>
                    <p className="placeholder col-12 mb-2"></p>
                    <p className="placeholder col-10 mb-2"></p>
                    <p className="placeholder col-8 mb-3"></p>
                    <div className="placeholder" style={{ width: '150px', height: '40px', borderRadius: '20px' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="provider-sec">
        <div className="container">
          <div 
            className="provider-box"
            style={{
              backgroundImage: 'url(/images/boxes/provider_bg.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'cover'
            }}
          >
            <div className="row">
              <div className="col-lg-12">
                <div className="content-box">
                  <h2>Are you a provider?</h2>
                  <p>
                    Providers can enroll in professional membership, giving you easy access to your agency's
                    listings for quick updates and edits.
                  </p>
                  <p>Join our provider resource directory of over 26,000 resources today!</p>
                  <p>If you have any questions please submit to data@virginianavigator.org</p>
                  <a href="/page/for-providers" className="btn btn-white">
                    <img src="/images/icons/black_arrow.svg" className="arrow-icon" alt="" />
                    Read More
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Helper to slugify text for URLs
  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Helper to get link or generate default
  const getLinkOrDefault = (link: string | null | undefined, label: string) => {
    if (link && link !== '' && link !== '#' && link !== '/') {
      return link;
    }
    return `/${currentLang}/page/${slugify(label)}`;
  };

  // Process API data
  const title = providerData?.data?.title || 'Are you a provider?';
  const content = providerData?.data?.content || '';
  const buttonText = providerData?.data?.specifications?.find((spec: any) => spec.key === 'button-text')?.value || 'Read More';
  const buttonLink = providerData?.data?.specifications?.find((spec: any) => spec.key === 'button-link')?.value || '#';

  // Decode and extract text from HTML content
  const decodedContent = decodeContent(content);
  const textContent = extractTextFromHtml(decodedContent);
  
  // Split content into paragraphs (assuming they're separated by line breaks or periods)
  const paragraphs = textContent.split(/(?<=\.)\s+/).filter(p => p.trim().length > 0);

  // Fallback content if API data is empty
  const fallbackParagraphs = [
    "Providers can enroll in professional membership, giving you easy access to your agency's listings for quick updates and edits.",
    "Join our provider resource directory of over 26,000 resources today!",
    "If you have any questions please submit to data@virginianavigator.org"
  ];

  const displayParagraphs = paragraphs.length > 0 ? paragraphs : fallbackParagraphs;

  return (
    <section className="provider-sec">
      <div className="container">
        <div 
          className="provider-box"
          style={{
            backgroundImage: 'url(/images/boxes/provider_bg.png)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        >
          <div className="row">
            <div className="col-lg-12">
              <div className="content-box">
                <h2>{title}</h2>
                {displayParagraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
                <Link href={getLinkOrDefault(buttonLink, title)} className="btn btn-white">
                  <img src="/images/icons/black_arrow.svg" className="arrow-icon" alt="" />
                  {buttonText}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProviderSection;
